import { getAdminPath } from '@doss/common-all'
import { ChevronRight } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FiExternalLink } from 'react-icons/fi'
import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { cn } from '@/lib/utils'
import constants from '~/common/config/config'
import {
  ActionStatus,
  ActionStepStatus,
  BreadcrumbObjectType,
} from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import { RelatedActionStep, RelatedActionSteps } from './types'

interface ActionGroup {
  actionId: string
  actionName: string
  actionStatus: ActionStatus
  workflowId: string
  workflowName: string
  steps: RelatedActionStep[]
}

// Group action steps by action ID
const groupStepsByAction = (steps: RelatedActionStep[]): ActionGroup[] => {
  const grouped = steps.reduce((acc, step) => {
    const actionId = step.rootAction.id
    const existingGroup = acc.get(actionId)

    if (existingGroup) {
      existingGroup.steps.push(step)
    } else {
      acc.set(actionId, {
        actionId,
        actionName: step.rootAction.name,
        actionStatus: step.rootAction.status,
        workflowId: step.rootAction.workflow.id,
        workflowName: step.rootAction.workflow.name,
        steps: [step],
      })
    }

    return acc
  }, new Map<string, ActionGroup>())

  return Array.from(grouped.values())
}

// Sort action groups by workflow name, then by action name
const sortActionGroups = (groups: ActionGroup[]): ActionGroup[] => {
  return groups.sort((a, b) => {
    const workflowCompare = a.workflowName.localeCompare(b.workflowName)
    if (workflowCompare !== 0) {
      return workflowCompare
    }

    return a.actionName.localeCompare(b.actionName)
  })
}

type RelatedActionStepsViewProps = {
  emptyStateMessage?: string
  entityName?: string
  readStepsTitle?: string
  readStepsDescription?: string
  relatedActionSteps: RelatedActionSteps
  writeStepsTitle?: string
  writeStepsDescription?: string
} & (
  | {
      showTableOfContents: true
      scrollContainerId: string
    }
  | {
      showTableOfContents?: false
      scrollContainerId?: never
    }
)

const ActionStatusChip = ({ status }: { status: ActionStepStatus }) => {
  const { t } = useTypedTranslation()

  switch (status) {
    case ActionStepStatus.ACTIVE:
      return <Chip variant="success">{t('table.related_action_steps.active')}</Chip>
    case ActionStepStatus.INACTIVE:
      return <Chip variant="error">{t('table.related_action_steps.inactive')}</Chip>
    case ActionStepStatus.FORCED_INACTIVE:
      return <Chip>{t('table.related_action_steps.forced_inactive')}</Chip>
    default:
      return null
  }
}

const ActionGroupHeader = ({
  actionId,
  actionName,
  actionStatus,
  user,
}: {
  actionId: string
  actionName: string
  actionStatus: ActionStatus
  user: ReturnType<typeof useGlobalContext>['user']
}) => {
  const { t } = useTypedTranslation()
  const actionUrl = `${constants.adminWebUrl}${getAdminPath(BreadcrumbObjectType.ACTION, actionId, user?.organizationId)}`

  return (
    <div className="flex min-w-0 items-center gap-2 border-b border-gray-4 bg-gray-2 px-3 py-2">
      <a
        className="min-w-[50px] flex-1 truncate text-sm text-gray-10 hover:text-gray-12"
        href={actionUrl}
        rel="noopener noreferrer"
        target="_blank"
      >
        {actionName}
      </a>
      <div className="flex min-w-0 items-center gap-2 overflow-hidden text-xs text-gray-10">
        <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
          <div
            className={cn(
              actionStatus === ActionStatus.ACTIVE ? 'bg-green-9' : 'bg-red-9',
              'h-2 w-2 shrink-0 rounded-full',
            )}
          />
          <span className="truncate">
            {actionStatus === ActionStatus.ACTIVE
              ? t('table.related_action_steps.active')
              : t('table.related_action_steps.inactive')}
          </span>
        </div>
      </div>
    </div>
  )
}

const ActionStepCard = ({
  step,
  user,
}: {
  step: RelatedActionStep
  user: ReturnType<typeof useGlobalContext>['user']
}) => {
  const { t } = useTypedTranslation()
  const snackbar = useDossSnackbar()

  const actionStepUrl = `${constants.adminWebUrl}${getAdminPath(BreadcrumbObjectType.ACTION_STEP, step.id, user?.organizationId)}`
  const workflowUrl = `${constants.adminWebUrl}${getAdminPath(BreadcrumbObjectType.WORKFLOW, step.rootAction.workflow.id, user?.organizationId)}`
  const actionUrl = `${constants.adminWebUrl}${getAdminPath(BreadcrumbObjectType.ACTION, step.rootAction.id, user?.organizationId)}`

  const handleCopyId = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    navigator.clipboard.writeText(step.id)
    snackbar.showSuccess(t('common.copied'))
  }

  return (
    <div
      className="group/related-action-step relative flex w-full cursor-pointer flex-col hover:bg-gray-1"
      onClick={() => window.open(actionStepUrl, '_blank')}
    >
      <div className="flex items-start justify-between gap-2 p-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="inline-flex min-w-0 items-center gap-2 text-sm text-gray-10">
            {step.name}
            <FiExternalLink
              className="invisible shrink-0 group-hover/related-action-step:visible"
              strokeWidth={1.8}
            />
          </div>
          <div className="flex flex-col gap-1 text-xs text-gray-8">
            <div className="truncate">{step.type}</div>
            <div className="flex min-w-0 items-center gap-1">
              <a
                className="min-w-0 truncate text-gray-8 hover:text-gray-10"
                href={workflowUrl}
                onClick={(e) => e.stopPropagation()}
                rel="noopener noreferrer"
                target="_blank"
              >
                {step.rootAction.workflow.name}
              </a>
              <span>|</span>
              <a
                className="min-w-0 truncate text-gray-8 hover:text-gray-10"
                href={actionUrl}
                onClick={(e) => e.stopPropagation()}
                rel="noopener noreferrer"
                target="_blank"
              >
                {step.rootAction.name}
              </a>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <ActionStatusChip status={step.status} />
          <Button
            className="invisible rounded px-2 py-1 group-hover/related-action-step:visible"
            onClick={handleCopyId}
            size="xs"
            variant="outline"
          >
            {t('table.related_action_steps.copy_id')}
          </Button>
        </div>
      </div>
    </div>
  )
}

// Hook to track which action is currently visible
const useVisibleAction = (actionIds: string[], scrollContainerId?: string) => {
  const [activeActionId, setActiveActionId] = useState<string | null>(null)
  const programmaticScrollRef = useRef(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!programmaticScrollRef.current) {
          const visibleEntry = entries.find((entry) => entry.isIntersecting)
          if (visibleEntry) {
            setActiveActionId(visibleEntry.target.getAttribute('data-action-id'))
          }
        }
      },
      {
        root: null,
        // Highlight section when it's in the upper-middle of the viewport
        rootMargin: '-20% 0px -70% 0px',
        threshold: 0,
      },
    )

    actionIds.forEach((id) => {
      const element = document.querySelector(`[data-action-id="${id}"]`)
      if (element) {
        observer.observe(element)
      }
    })

    return () => observer.disconnect()
  }, [actionIds])

  useEffect(() => {
    if (!scrollContainerId) {
      return
    }

    const container = document.getElementById(scrollContainerId)
    if (!container) {
      return
    }

    const handleScrollEnd = () => {
      programmaticScrollRef.current = false
    }

    container.addEventListener('scrollend', handleScrollEnd)

    return () => {
      container.removeEventListener('scrollend', handleScrollEnd)
    }
  }, [scrollContainerId])

  const handleActionClick = (actionId: string) => {
    setActiveActionId(actionId)
    programmaticScrollRef.current = true

    document.querySelector(`[data-action-id="${actionId}"]`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  return { activeActionId, handleActionClick }
}

interface TOCSection {
  id: string
  title: string
  actions: Array<{
    id: string
    name: string
  }>
}

const TableOfContents = ({
  sections,
  activeActionId,
  onActionClick,
}: {
  sections: TOCSection[]
  activeActionId: string | null
  onActionClick: (actionId: string) => void
}) => {
  // Initialize all sections as expanded
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set(sections.map((s) => s.id)),
  )

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(sectionId)) {
        next.delete(sectionId)
      } else {
        next.add(sectionId)
      }
      return next
    })
  }

  return (
    <nav className="sticky top-6 hidden h-fit w-64 shrink-0 py-2 lg:block">
      <div className="text-xs">
        {sections.map((section) => {
          const isExpanded = expandedSections.has(section.id)
          return (
            <div key={section.id} className="mb-4">
              <button
                className="mb-1.5 flex w-full items-center gap-1.5 font-semibold text-gray-10 hover:text-gray-12"
                onClick={() => toggleSection(section.id)}
                type="button"
              >
                <ChevronRight
                  className={cn('h-3 w-3 shrink-0 transition-transform', isExpanded && 'rotate-90')}
                  strokeWidth={2.2}
                />
                {section.title}
              </button>
              {isExpanded && (
                <ul className="space-y-1">
                  {section.actions.map((action) => (
                    <li key={action.id}>
                      <button
                        className={cn(
                          'ms-4 text-gray-8 hover:text-gray-10 w-full text-left transition-colors',
                          activeActionId === action.id && 'text-gray-10',
                        )}
                        onClick={() => onActionClick(action.id)}
                        type="button"
                      >
                        <span className="line-clamp-2">{action.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        })}
      </div>
    </nav>
  )
}

const RelatedActionStepsSection = ({
  title,
  description,
  actionGroups,
  sectionId,
}: {
  title: string
  description: string
  actionGroups: ActionGroup[]
  sectionId: string
}) => {
  const { user } = useGlobalContext()

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="mb-1 text-sm font-semibold">{title}</h3>
        <p className="text-xs text-gray-8">{description}</p>
      </div>
      <div className="flex flex-col gap-4">
        {actionGroups.map((group) => (
          <div
            key={group.actionId}
            className="flex flex-col rounded border border-gray-4"
            data-action-id={`${sectionId}-${group.actionId}`}
          >
            <ActionGroupHeader
              actionId={group.actionId}
              actionName={group.actionName}
              actionStatus={group.actionStatus}
              user={user}
            />
            <div className="flex flex-col divide-y">
              {group.steps.map((step) => (
                <ActionStepCard key={step.id} step={step} user={user} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export const RelatedActionStepsView = ({
  emptyStateMessage,
  entityName,
  readStepsTitle,
  readStepsDescription,
  relatedActionSteps,
  scrollContainerId,
  showTableOfContents = false,
  writeStepsTitle,
  writeStepsDescription,
}: RelatedActionStepsViewProps) => {
  const { t } = useTypedTranslation()
  const { readSteps, writeSteps } = relatedActionSteps
  const hasNoDependencies = readSteps.length === 0 && writeSteps.length === 0

  const entityNameText = entityName ?? t('table.related_action_steps.entity')

  const readTitle = readStepsTitle ?? t('table.related_action_steps.read_steps')
  const readDescription =
    readStepsDescription ??
    t('table.related_action_steps.read_steps_description', { entityType: entityNameText })
  const writeTitle = writeStepsTitle ?? t('table.related_action_steps.write_steps')
  const writeDescription =
    writeStepsDescription ??
    t('table.related_action_steps.write_steps_description', { entityType: entityNameText })
  const emptyMessage =
    emptyStateMessage ??
    t('table.related_action_steps.no_dependencies', { entityType: entityNameText })

  const readActionGroups = useMemo(
    () => (readSteps.length > 0 ? sortActionGroups(groupStepsByAction(readSteps)) : []),
    [readSteps],
  )
  const writeActionGroups = useMemo(
    () => (writeSteps.length > 0 ? sortActionGroups(groupStepsByAction(writeSteps)) : []),
    [writeSteps],
  )

  const tocSections: TOCSection[] = useMemo(() => {
    const sections: TOCSection[] = []

    if (readActionGroups.length > 0) {
      sections.push({
        id: 'read',
        title: readTitle,
        actions: readActionGroups.map((group) => ({
          id: `read-${group.actionId}`,
          name: group.actionName,
        })),
      })
    }

    if (writeActionGroups.length > 0) {
      sections.push({
        id: 'write',
        title: writeTitle,
        actions: writeActionGroups.map((group) => ({
          id: `write-${group.actionId}`,
          name: group.actionName,
        })),
      })
    }

    return sections
  }, [readActionGroups, writeActionGroups, readTitle, writeTitle])

  // Get all action IDs for IntersectionObserver
  const allActionIds = useMemo(
    () => tocSections.flatMap((section) => section.actions.map((a) => a.id)),
    [tocSections],
  )

  const { activeActionId, handleActionClick } = useVisibleAction(allActionIds, scrollContainerId)

  if (hasNoDependencies) {
    return <div className="px-1 py-2 text-xs text-gray-9">{emptyMessage}</div>
  }

  // Render with TOC if enabled and there are sections
  if (showTableOfContents && tocSections.length > 0) {
    return (
      <div className="flex min-w-0 flex-1 gap-16">
        <TableOfContents
          activeActionId={activeActionId}
          onActionClick={handleActionClick}
          sections={tocSections}
        />

        <div className="max-w- flex min-w-0 max-w-[800px] flex-1 flex-col gap-10 px-1 py-2">
          {readActionGroups.length > 0 && (
            <RelatedActionStepsSection
              actionGroups={readActionGroups}
              description={readDescription}
              sectionId="read"
              title={readTitle}
            />
          )}

          {writeActionGroups.length > 0 && (
            <RelatedActionStepsSection
              actionGroups={writeActionGroups}
              description={writeDescription}
              sectionId="write"
              title={writeTitle}
            />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex max-w-[800px] flex-1 flex-col gap-6 px-1 py-2">
      {readActionGroups.length > 0 && (
        <RelatedActionStepsSection
          actionGroups={readActionGroups}
          description={readDescription}
          sectionId="read"
          title={readTitle}
        />
      )}

      {writeActionGroups.length > 0 && (
        <RelatedActionStepsSection
          actionGroups={writeActionGroups}
          description={writeDescription}
          sectionId="write"
          title={writeTitle}
        />
      )}
    </div>
  )
}
